/**
 * Resource icons, drawn in code.
 *
 * `RESOURCE_GLYPHS` in `sim/types.ts` gives every resource a single Unicode
 * character, which was the right call for a cost written inside a sentence and
 * the wrong one for the strip along the top of the screen. Blown up, that strip
 * reads: a squiggle, three lines, a pentagon, crossed hammers, a clover, a
 * quarter-filled circle, a circled dot — seven marks in one colour, two of which
 * (`⬟` stone and `☗` population) are the same shape at the same size. A player
 * cannot learn that. They can learn a log, a rock and a loaf in one glance,
 * which is the entire argument for this file.
 *
 * Faceted rather than flat, in keeping with everything else drawn here: two or
 * three planes per solid, a lit one and a shaded one, so a 15-pixel mark still
 * reads as an object with a top and a side rather than as a sticker. The tints
 * come out of `data/palette.ts` where one fits and are stated locally where the
 * palette has nothing — grain has no entry because nothing in the *world* is
 * the colour of wheat.
 *
 * **Shape families, not twenty-three drawings.** The recipe ladder runs to
 * twenty-three resources and most of them are late-Age crystals a player meets
 * once. They share a faceted-gem shape and are told apart by tint; the eight a
 * first hour actually turns on get a drawing of their own. A resource with no
 * entry here falls back to its Unicode glyph, so adding one to the ladder never
 * breaks a screen — it just does not get a picture until somebody draws it.
 */

import type { ResourceKey } from '../sim/types.ts';
import { RESOURCE_GLYPHS } from '../sim/types.ts';

/** Which drawing a resource uses. */
type Family = 'log' | 'plank' | 'rock' | 'ingot' | 'wheat' | 'loaf' | 'coin' | 'skull' | 'gem';

/** `[family, lit face, shaded face]`. The third is the underside of the solid. */
const ICONS: Partial<Record<ResourceKey, [Family, string, string]>> = {
  timber: ['log', '#8a6135', '#573b1f'],
  planks: ['plank', '#a8813f', '#6b4f24'],
  stone: ['rock', '#8d8579', '#565049'],
  iron: ['ingot', '#8e939b', '#565b63'],
  grain: ['wheat', '#c9a94e', '#8a7130'],
  bread: ['loaf', '#b07c3e', '#734d24'],
  gold: ['coin', '#e6cf92', '#8a6c26'],
  souls: ['skull', '#c3d6e4', '#69808f'],

  // The ladder past the second Age. One shape, told apart by tint — which is
  // how they are told apart in the recipes too.
  essence: ['gem', '#7fa8d4', '#41618a'],
  magewood: ['log', '#6f7f94', '#404a5c'],
  runestone: ['rock', '#8e86a6', '#544e68'],
  ingots: ['ingot', '#b9b2a2', '#6f6a5d'],
  soulwood: ['log', '#7d8a7a', '#48524a'],
  voidstone: ['gem', '#6b5f8e', '#3b3355'],
  alloy: ['ingot', '#c2a86a', '#7a663a'],
  quintessence: ['gem', '#e6cf92', '#8a6c26'],
  ashglass: ['gem', '#a9b3b8', '#606a70'],
  heartiron: ['ingot', '#a8555a', '#642f33'],
  hollowglass: ['gem', '#b9d3e6', '#6c8697'],
  gravesilver: ['ingot', '#c6cbd2', '#767d87'],
  ichor: ['gem', '#9c3336', '#5a1c1e'],
  grudges: ['coin', '#b08a4e', '#6a5127'],
  scrap: ['ingot', '#7e7466', '#4b453b'],
};

/**
 * The drawings. Every one is authored on a 16×16 grid with two fills: `A` is
 * the lit plane, `B` the shaded one. No strokes — a hairline stroke at this size
 * either vanishes or eats the whole shape depending on the display, and this
 * game has to look the same on both.
 */
const SHAPES: Record<Family, string> = {
  // Three logs end-on, stacked two and one. **Round**, and that is the whole
  // point: the first cut drew them as stacked rectangles, which is exactly what
  // `plank` is, and the two chips sit side by side on the strip. Bark is the
  // shade, the sawn face is the light.
  log:
    '<path fill="B" d="M4.5 6.6a3.3 3.3 0 1 1 0 6.6 3.3 3.3 0 0 1 0-6.6z"/>' +
    '<path fill="B" d="M11.5 6.6a3.3 3.3 0 1 1 0 6.6 3.3 3.3 0 0 1 0-6.6z"/>' +
    '<path fill="B" d="M8 1.5a3.3 3.3 0 1 1 0 6.6 3.3 3.3 0 0 1 0-6.6z"/>' +
    '<path fill="A" d="M4.5 8.2a1.7 1.7 0 1 1 0 3.4 1.7 1.7 0 0 1 0-3.4z"/>' +
    '<path fill="A" d="M11.5 8.2a1.7 1.7 0 1 1 0 3.4 1.7 1.7 0 0 1 0-3.4z"/>' +
    '<path fill="A" d="M8 3.1a1.7 1.7 0 1 1 0 3.4 1.7 1.7 0 0 1 0-3.4z"/>',
  // Boards, seen along the edge. Flat by nature, so the read is the stack.
  plank:
    '<path fill="A" d="M1 4h12v2.4H1z"/><path fill="B" d="M13 4l2-1.4v2.4L13 6.4z"/>' +
    '<path fill="A" d="M1 7.3h12v2.4H1z"/><path fill="B" d="M13 7.3l2-1.4v2.4l-2 1.4z"/>' +
    '<path fill="A" d="M1 10.6h12V13H1z"/><path fill="B" d="M13 10.6l2-1.4V11.6L13 13z"/>',
  // A boulder split down its ridge. The two faces *tile* the silhouette rather
  // than overlapping it: at fifteen pixels an overlap is a third colour, and a
  // third colour on a shape this small is a blob.
  rock:
    '<path fill="A" d="M2 13.2 3.6 6.8 7.4 3.2 9.6 13.2z"/>' +
    '<path fill="B" d="M9.6 13.2 7.4 3.2 11.4 4.8 14 10.4 13.6 13.2z"/>',
  // A cast bar, three-quarters on. Three planes — top, front, end — because a
  // bar drawn flat on is a rectangle and a rectangle is not a thing.
  ingot:
    '<path fill="A" d="M2 7.4 4.8 4.8h9.2L11.2 7.4z"/>' +
    '<path fill="B" d="M2 7.4h9.2v4.2H2z"/>' +
    '<path fill="B" d="M11.2 7.4 14 4.8v4.2l-2.8 2.6z" opacity="0.62"/>',
  // An ear on a stalk. Six grains, alternating faces, so it reads as a sheaf
  // and not as a leaf.
  wheat:
    '<path fill="B" d="M7.6 14V6h.9v8z"/>' +
    '<path fill="A" d="M8 5.2 6 3.4 8 2l2 1.4z"/>' +
    '<path fill="A" d="M7.7 8.2 4.6 6.6 5.2 4.4 7.7 6z"/>' +
    '<path fill="B" d="M8.4 8.2 11.5 6.6 10.9 4.4 8.4 6z"/>' +
    '<path fill="A" d="M7.7 11.4 4.6 9.8 5.2 7.6 7.7 9.2z"/>' +
    '<path fill="B" d="M8.4 11.4 11.5 9.8 10.9 7.6 8.4 9.2z"/>',
  // A risen loaf with three cuts across the crown. The cuts are the lit face and
  // they are wide slashes, not lines: a one-pixel score disappears at this size
  // and takes the whole read of the icon with it.
  loaf:
    '<path fill="B" d="M1.4 12.6c0-4.8 2.7-8 6.6-8s6.6 3.2 6.6 8z"/>' +
    '<path fill="A" d="M4 10.6 5.8 6.9h1.5L5.5 10.6z"/>' +
    '<path fill="A" d="M6.9 10.6 8.7 6.3h1.5L8.4 10.6z"/>' +
    '<path fill="A" d="M9.8 10.6 11.4 7.1h1.4L11.3 10.6z"/>' +
    '<path fill="B" d="M0.8 12.6h14.4v1.6H0.8z" opacity="0.75"/>',
  // A struck coin: rim, face, and one bevelled quarter.
  //
  // The bevel is a *quarter of the face*, not two opposite quarters — the first
  // cut lit the north-east and the south-west and the result was unmistakably a
  // pie chart, which is a thing a strategy game's UI must never accidentally
  // draw.
  coin:
    '<path fill="B" d="M8 1.4a6.6 6.6 0 1 1 0 13.2A6.6 6.6 0 0 1 8 1.4z"/>' +
    '<path fill="A" d="M8 3.2a4.8 4.8 0 1 1 0 9.6 4.8 4.8 0 0 1 0-9.6z"/>' +
    '<path fill="B" d="M8 3.2a4.8 4.8 0 0 1 4.8 4.8H8z" opacity="0.3"/>',
  // A skull, front on. Two sockets and a jaw is all it takes at this size.
  skull:
    '<path fill="B" d="M8 1.8c3.3 0 5.6 2.3 5.6 5.5 0 2-.9 3.2-2 3.9v2.2H4.4V11.2C3.3 10.5 2.4 9.3 2.4 7.3c0-3.2 2.3-5.5 5.6-5.5z"/>' +
    '<path fill="A" d="M5.9 6.1c.9 0 1.5.7 1.5 1.6s-.6 1.5-1.5 1.5-1.6-.6-1.6-1.5.7-1.6 1.6-1.6z" opacity="0.25"/>' +
    '<path fill="A" d="M10.1 6.1c.9 0 1.6.7 1.6 1.6s-.7 1.5-1.6 1.5-1.5-.6-1.5-1.5.6-1.6 1.5-1.6z" opacity="0.25"/>' +
    '<path fill="A" d="M8 9.7l1 1.9H7z" opacity="0.3"/>',
  // A cut stone: crown, table and pavilion, which is the fewest planes that
  // still says "faceted" rather than "hexagon".
  gem:
    '<path fill="A" d="M5 2.4h6l2.6 3.6H2.4z"/>' +
    '<path fill="B" d="M2.4 6h11.2L8 14z"/>' +
    '<path fill="A" d="M5 2.4 6.6 6H2.4z" opacity="0.55"/>' +
    '<path fill="A" d="M8 14 6.6 6h2.8z" opacity="0.28"/>',
};

/**
 * Population, which is not a resource and has never had a decent mark.
 *
 * `☗` is a pentagon with a line in it and sits three chips away from `⬟`, the
 * pentagon that means stone. Two figures, one behind the other.
 */
const FOLK =
  '<path fill="#6d675e" d="M10.6 3.2a1.7 1.7 0 1 1 0 3.4 1.7 1.7 0 0 1 0-3.4z"/>' +
  '<path fill="#6d675e" d="M8.4 13.4V10c0-1.5 1-2.5 2.2-2.5s2.4 1 2.4 2.5v3.4z"/>' +
  '<path fill="#a89e8c" d="M6 2.4a2 2 0 1 1 0 4 2 2 0 0 1 0-4z"/>' +
  '<path fill="#a89e8c" d="M2.6 13.4V9.8c0-1.8 1.3-3 3.4-3s3.4 1.2 3.4 3v3.6z"/>';

/**
 * One icon as an inline `<svg>` string.
 *
 * Returns the Unicode glyph in a `<span>` for anything without a drawing, so a
 * caller never has to ask which resources are covered — every key returns
 * something that renders inside a `.glyph` box at the same size.
 *
 * `aria-hidden`, and the value beside it carries the label: a screen reader
 * that announced "path path path" before every number would make the strip
 * worse than the one this replaces.
 */
export function resourceIcon(k: ResourceKey, px = 15): string {
  const spec = ICONS[k];
  if (!spec) return `<span class="rglyph">${RESOURCE_GLYPHS[k]}</span>`;
  const [family, lit, shade] = spec;
  const body = SHAPES[family].split('fill="A"').join(`fill="${lit}"`).split('fill="B"').join(`fill="${shade}"`);
  return (
    `<svg class="ricon" width="${px}" height="${px}" viewBox="0 0 16 16" aria-hidden="true">${body}</svg>`
  );
}

/** The people, in the same box as the resources. */
export function folkIcon(px = 15): string {
  return `<svg class="ricon" width="${px}" height="${px}" viewBox="0 0 16 16" aria-hidden="true">${FOLK}</svg>`;
}
